import React, { Component } from 'react';
import {
    Box,
    Typography,
    Avatar,
    IconButton,
    TextField,
    Button,
    Card,
    CardMedia
} from '@mui/material';
import { Close, Send, ShoppingBag, VolumeUp, VolumeOff, Favorite, PushPin } from '@mui/icons-material';
import Hls from 'hls.js';
import { io } from 'socket.io-client';
import { apiUrl } from '../../comp/ApiUrl';

const SOCKET_SERVER_URL = apiUrl; // Subsitua pelo endpoint do seu servidor Node.js

class LivePage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            chatInput: '',
            chatMensagens: [],
            lojaSelecionada: props.lojaSelecionada || {
                slug: 'loja_01',
                nome: 'Teste Loja',
                logo: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=150'
            },
            espectadores: 0,
            curtidas: 0,
            produtoFixado: null,
            isMuted: false,
            showUnmuteBanner: false,
            statusTexto: 'Conectando...',
            coracoes: []
        };

        this.videoRef = React.createRef();
        this.chatEndRef = React.createRef();
        this.hlsInstance = null;
        this.peerConnection = null;
        this.reconnectTimeout = null;
        this.socket = null;
    }

    componentDidMount() {
        this.carregarCanal();
        this.conectarSocket();
    }

    componentWillUnmount() {
        this.limparConexao();
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.chatMensagens.length !== this.state.chatMensagens.length) {
            this.scrollToBottomChat();
        }
    }


    conectarSocket = () => {
        const canal = this.state.lojaSelecionada?.slug || 'loja_01';

        // 1. Obtém o token salvo (no localStorage, sessionStorage ou via props)
        const token = localStorage.getItem('auth_token') || this.props.token || '';

        // 2. Inicializa o socket passando as credenciais no objeto 'auth'
        this.socket = io(SOCKET_SERVER_URL, {
            transports: ['websocket'],
            withCredentials: true, // Garante o envio automático de cookies HttpOnly se estiver na mesma origem
            auth: {
                type: 'mobile'        // Define o tipo ('web' ou 'mobile')
            }
        });

        // Tratamento de erro de autenticação vindo do middleware do backend
        this.socket.on('connect_error', (err) => {
            console.error('Falha na autenticação do Socket:', err.message);
            this.setState({ statusTexto: `Erro de Conexão: ${err.message}` });
        });

        // 3. Evento disparado após autenticação com sucesso no middleware
        this.socket.on('connect', () => {
            // Envia os dados da sala utilizando os dados já validados no socket no servidor
            this.socket.emit('entrar_live', {
                canal,
                usuario: this.socket.id ? `Usuário #${this.socket.id}` : 'Espectador',
                isLojista: false
            });
        });

        // 4. Listeners dos eventos do Socket
        this.socket.on('estado_inicial_live', (data) => {
            this.setState({
                curtidas: data.curtidas || 0,
                produtoFixado: data.produtoFixado || null,
                chatMensagens: data.mensagens || []
            });
        });

        this.socket.on('nova_mensagem', (mensagem) => {
            this.setState((prevState) => ({
                chatMensagens: [...prevState.chatMensagens, mensagem]
            }));
        });

        this.socket.on('atualizar_espectadores', ({ espectadores }) => {
            this.setState({ espectadores });
        });

        this.socket.on('atualizar_curtidas', ({ curtidas }) => {
            this.setState({ curtidas });
        });

        this.socket.on('produto_fixado_alterado', ({ produto }) => {
            this.setState({ produtoFixado: produto });
        });
    };

    scrollToBottomChat = () => {
        if (this.chatEndRef.current) {
            this.chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    limparConexao = () => {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }
        if (this.hlsInstance) {
            this.hlsInstance.destroy();
            this.hlsInstance = null;
        }
    };

    carregarCanal = async () => {
        const video = this.videoRef.current;
        if (!video) return;

        this.limparConexao();

        const canal = this.state.lojaSelecionada?.slug || 'loja_01';
        const whepUrl = `https://whip.fazpedido.app/${canal}/whep`;

        this.setState({ statusTexto: `Conectando ao canal ${canal} via WHEP...` });

        try {
            this.peerConnection = new RTCPeerConnection({
                iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
            });

            this.peerConnection.addTransceiver('video', { direction: 'recvonly' });
            this.peerConnection.addTransceiver('audio', { direction: 'recvonly' });

            this.peerConnection.ontrack = (event) => {
                if (video.srcObject !== event.streams[0]) {
                    video.srcObject = event.streams[0];
                    this.setState({ statusTexto: `AO VIVO (${canal})` });
                    this.tentarReproduzirComSom();
                }
            };

            const offer = await this.peerConnection.createOffer();
            await this.peerConnection.setLocalDescription(offer);

            const response = await fetch(whepUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/sdp' },
                body: offer.sdp
            });

            if (!response.ok) {
                throw new Error('WHEP indisponível, migrando para modo HLS...');
            }

            const answerSdp = await response.text();
            await this.peerConnection.setRemoteDescription({ type: 'answer', sdp: answerSdp });

        } catch (err) {
            console.warn('WHEP Falhou, tentando fallback HLS:', err);
            this.carregarHLS(canal);
        }
    };

    carregarHLS = (canal) => {
        const video = this.videoRef.current;
        if (!video) return;

        const hlsUrl = `https://live.fazpedido.app/${canal}/stream.m3u8`;
        this.setState({ statusTexto: `Conectando ao canal ${canal} (HLS)...` });

        if (Hls.isSupported()) {
            this.hlsInstance = new Hls({
                lowLatencyMode: true,
                backBufferLength: 10,
                maxBufferLength: 10,
                maxMaxBufferLength: 20,
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 6,
                enableWorker: true
            });

            this.hlsInstance.loadSource(hlsUrl);
            this.hlsInstance.attachMedia(video);

            this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
                this.setState({ statusTexto: `AO VIVO (${canal})` });
                this.tentarReproduzirComSom();
            });

            this.hlsInstance.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    this.setState({ statusTexto: 'Reconectando transmissão...' });
                    this.reconnectTimeout = setTimeout(() => this.carregarCanal(), 3000);
                }
            });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = hlsUrl;
            video.addEventListener('loadedmetadata', () => {
                this.tentarReproduzirComSom();
            });
        }
    };

    tentarReproduzirComSom = () => {
        const video = this.videoRef.current;
        if (!video) return;

        video.muted = false;

        video.play().catch(() => {
            video.muted = true;
            this.setState({ isMuted: true, showUnmuteBanner: true });
            video.play().catch(e => console.log('Erro no autoplay:', e));
        });
    };

    ativarSom = () => {
        const video = this.videoRef.current;
        if (video) {
            video.muted = false;
            this.setState({ isMuted: false, showUnmuteBanner: false });
        }
    };

    toggleAudio = () => {
        const video = this.videoRef.current;
        if (video) {
            video.muted = !video.muted;
            this.setState({ isMuted: video.muted });
        }
    };

    fecharLive = () => {
        if (this.props.onClose) {
            this.props.onClose();
        } else {
            window.history.back();
        }
    };

    enviarMensagemChat = () => {
        const { chatInput, lojaSelecionada } = this.state;
        if (!chatInput.trim() || !this.socket) return;

        const canal = lojaSelecionada?.slug || 'loja_01';

        // Envia mensagem via WebSocket para transmissão geral
        this.socket.emit('enviar_mensagem', {
            canal,
            texto: chatInput.trim()
        });

        this.setState({ chatInput: '' });
    };

    abrirLoja = (loja) => {
        console.log('Abrir catálogo da loja:', loja);
    };

    handleTapTap = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const novoCoracao = {
            id: Date.now() + Math.random(),
            x,
            y
        };

        this.setState((prevState) => ({
            coracoes: [...prevState.coracoes, novoCoracao]
        }));

        // Notifica o servidor via socket sobre o toque/curtida
        if (this.socket) {
            this.socket.emit('enviar_curtida', {
                canal: this.state.lojaSelecionada?.slug || 'loja_01',
                quantidade: 1
            });
        }

        setTimeout(() => {
            this.setState((prevState) => ({
                coracoes: prevState.coracoes.filter((c) => c.id !== novoCoracao.id)
            }));
        }, 1000);
    };

    render() {
        const {
            lojaSelecionada,
            chatMensagens,
            chatInput,
            espectadores,
            isMuted,
            showUnmuteBanner,
            coracoes,
            produtoFixado
        } = this.state;

        return (
            <Box
                sx={{
                    position: 'relative',
                    width: '100vw',
                    height: '100vh',
                    maxWidth: '480px',
                    mx: 'auto',
                    bgcolor: '#000',
                    color: '#fff',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    userSelect: 'none'
                }}
            >
                <Box
                    component="video"
                    ref={this.videoRef}
                    autoPlay
                    playsInline
                    controls={false}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 1
                    }}
                />

                {/* Camada transparente que captura os toques (Tap Tap) */}
                <Box
                    onClick={this.handleTapTap}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 2,
                        cursor: 'pointer'
                    }}
                />

                {/* Corações flutuantes animados */}
                {coracoes.map((coracao) => (
                    <Box
                        key={coracao.id}
                        sx={{
                            position: 'absolute',
                            left: coracao.x - 20,
                            top: coracao.y - 20,
                            zIndex: 10,
                            pointerEvents: 'none',
                            color: '#ff4757',
                            animation: 'floatUpAndFade 1s forwards ease-out',
                            '@keyframes floatUpAndFade': {
                                '0%': { opacity: 1, transform: 'translateY(0) scale(0.8)' },
                                '50%': { opacity: 0.9, transform: 'translateY(-40px) scale(1.3)' },
                                '100%': { opacity: 0, transform: 'translateY(-80px) scale(1)' }
                            }
                        }}
                    >
                        <Favorite sx={{ fontSize: 40, filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }} />
                    </Box>
                ))}

                {showUnmuteBanner && (
                    <Box
                        onClick={this.ativarSom}
                        sx={{
                            position: 'absolute',
                            top: '40%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            p: 2,
                            px: 3,
                            borderRadius: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            cursor: 'pointer',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                        }}
                    >
                        <VolumeUp sx={{ color: '#ff4757', fontSize: 28 }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                            Toque para ativar o som
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 30%, transparent 60%, rgba(0,0,0,0.85) 100%)',
                        zIndex: 3,
                        pointerEvents: 'none'
                    }}
                />

                {/* Header Superior */}
                <Box sx={{ position: 'relative', zIndex: 4, p: 2, pt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(0,0,0,0.4)', p: 0.8, pr: 1.5, borderRadius: '30px' }}>
                        <Avatar src={lojaSelecionada?.logo} sx={{ width: 36, height: 36 }} />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                {lojaSelecionada?.nome}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#ff4757', fontWeight: 700 }}>
                                ● AO VIVO ({espectadores} {espectadores === 1 ? 'pessoa' : 'pessoas'})
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={this.toggleAudio} sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
                            {isMuted ? <VolumeOff /> : <VolumeUp />}
                        </IconButton>
                        <IconButton onClick={this.fecharLive} sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
                            <Close />
                        </IconButton>
                    </Box>
                </Box>

                {/* Overlay do Produto Fixado em Destaque */}
                {produtoFixado && (
                    <Box sx={{ position: 'relative', zIndex: 4, px: 2, mt: 'auto', mb: 1 }}>
                        <Card
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: 'rgba(0, 0, 0, 0.75)',
                                backdropFilter: 'blur(8px)',
                                color: '#fff',
                                borderRadius: '16px',
                                p: 1,
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}
                        >
                            <CardMedia component="img" sx={{ width: 56, height: 56, borderRadius: '10px', objectFit: 'cover' }} image={produtoFixado.img} alt={produtoFixado.nome} />
                            <Box sx={{ flexGrow: 1, ml: 1.5, overflow: 'hidden' }}>
                                <Typography variant="caption" sx={{ color: '#ff4757', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PushPin sx={{ fontSize: 12 }} /> PRODUTO EM DESTAQUE
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {produtoFixado.nome}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ color: '#2ed573', fontWeight: 800 }}>
                                    {produtoFixado.preco}
                                </Typography>
                            </Box>
                        </Card>
                    </Box>
                )}

                {/* Área Inferior: Chat e Ações */}
                <Box sx={{ position: 'relative', zIndex: 4, p: 2, pb: 3 }}>
                    <Box sx={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, mb: 2, '&::-webkit-scrollbar': { display: 'none' } }}>
                        {chatMensagens.map((msg) => (
                            <Box key={msg.id} sx={{ bgcolor: msg.isLojista ? 'rgba(86, 45, 171, 0.85)' : 'rgba(0,0,0,0.5)', p: 1, px: 1.5, borderRadius: '12px', alignSelf: 'flex-start', maxWidth: '85%' }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: msg.isLojista ? '#2ed573' : '#ffb142', mr: 0.8 }}>
                                    {msg.user || msg.usuario}:
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#fff' }}>
                                    {msg.text || msg.texto}
                                </Typography>
                            </Box>
                        ))}
                        <div ref={this.chatEndRef} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Comentar na live..."
                            value={chatInput}
                            onChange={(e) => this.setState({ chatInput: e.target.value })}
                            onKeyPress={(e) => e.key === 'Enter' && this.enviarMensagemChat()}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: '25px',
                                input: { color: '#fff', fontSize: '13px' },
                                '& fieldset': { border: 'none' },
                                '& .MuiInputBase-root': { borderRadius: '25px', px: 2 }
                            }}
                        />
                        <IconButton onClick={this.enviarMensagemChat} sx={{ bgcolor: '#562dab', color: '#fff', '&:hover': { bgcolor: '#452389' } }}>
                            <Send fontSize="small" />
                        </IconButton>
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ShoppingBag />}
                        onClick={() => this.abrirLoja(lojaSelecionada)}
                        sx={{
                            bgcolor: '#562dab',
                            color: '#fff',
                            fontWeight: 800,
                            py: 1.5,
                            borderRadius: '14px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#452389' }
                        }}
                    >
                        Ver Catálogo & Pedir
                    </Button>
                </Box>
            </Box>
        );
    }
}

export default LivePage;